/**
 * 骨架屏元件 | Skeleton Loading Components
 * 提供載入時的佔位動畫，提升使用者體驗
 */

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

/**
 * 基礎骨架屏元件
 */
export function Skeleton({
  className = '',
  variant = 'rectangular',
  width,
  height,
  animation = 'pulse',
}: SkeletonProps) {
  const baseClasses = 'skeleton-loading';

  const variantClasses = {
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-md',
    rounded: 'rounded-xl',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  const animationClass = animation === 'none' ? '' : 'animate-pulse';

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${animationClass} ${className}`}
      style={style}
      aria-hidden="true"
      role="presentation"
    />
  );
}

/**
 * 卡片骨架屏 - 用於列表項目
 */
interface CardSkeletonProps {
  showAvatar?: boolean;
  lines?: number;
}

export function CardSkeleton({ showAvatar = false, lines = 3 }: CardSkeletonProps) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
      {showAvatar && (
        <div className="flex items-center gap-3">
          <Skeleton variant="circular" width={40} height={40} />
          <div className="flex-1 space-y-2">
            <Skeleton width="60%" height={16} />
            <Skeleton width="40%" height={14} />
          </div>
        </div>
      )}
      <div className="space-y-2">
        <Skeleton width="100%" height={20} />
        <Skeleton width="90%" height={16} />
        {lines > 2 && <Skeleton width="80%" height={16} />}
        {lines > 3 && <Skeleton width="70%" height={16} />}
      </div>
    </div>
  );
}

/**
 * 表格骨架屏 - 用於表格載入
 */
export function TableSkeleton({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* 表頭 */}
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-3">
        <div className="flex gap-4">
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton key={i} width={`${100 / columns}%`} height={20} />
          ))}
        </div>
      </div>

      {/* 表格內容 */}
      <div className="divide-y divide-slate-100">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="px-4 py-3">
            <div className="flex gap-4">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <Skeleton key={colIndex} width={`${100 / columns}%`} height={16} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * 統計卡片骨架屏 - 用於儀表板
 */
export function StatsCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton variant="circular" width={48} height={48} />
        <Skeleton width={80} height={24} />
      </div>
      <Skeleton width="60%" height={16} />
      <Skeleton width="40%" height={14} />
    </div>
  );
}

/**
 * 列表骨架屏 - 用於清單載入
 */
export function ListSkeleton({ items = 5 }: { items?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: items }).map((_, i) => (
        <CardSkeleton key={i} showAvatar={false} lines={2} />
      ))}
    </div>
  );
}

/**
 * 詳細頁面骨架屏
 */
export function DetailPageSkeleton() {
  return (
    <div className="space-y-6">
      {/* 標題區域 */}
      <div className="space-y-3">
        <Skeleton width="40%" height={32} />
        <Skeleton width="60%" height={20} />
      </div>

      {/* 統計卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <StatsCardSkeleton key={i} />
        ))}
      </div>

      {/* 主要內容 */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <Skeleton width="30%" height={24} />
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} width="100%" height={16} />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * 甘特圖骨架屏
 */
export function GanttSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* 控制列 */}
      <div className="border-b border-slate-200 p-4">
        <Skeleton width="30%" height={24} />
      </div>

      {/* 甘特圖內容 */}
      <div className="p-4 space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton width={120} height={32} />
            <Skeleton width="100%" height={32} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * 表單骨架屏
 */
export function FormSkeleton({ fields = 4 }: { fields?: number }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
      <div className="space-y-3">
        <Skeleton width="40%" height={28} />
        <Skeleton width="70%" height={16} />
      </div>

      <div className="space-y-4">
        {Array.from({ length: fields }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton width="25%" height={16} />
            <Skeleton width="100%" height={48} />
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Skeleton width={100} height={44} />
        <Skeleton width={120} height={44} />
      </div>
    </div>
  );
}

/**
 * 頁面載入中狀態
 */
export function PageLoading({ message = '載入中...' }: { message?: string }) {
  return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="text-center space-y-4">
        {/* 雙環載入動畫 */}
        <div className="relative inline-block">
          <div className="w-16 h-16 border-4 border-blue-500/20 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p className="text-slate-600 text-sm font-medium">{message}</p>
      </div>
    </div>
  );
}

/**
 * 內容載入中狀態 - 較小的載入指示器
 */
export function ContentLoading({ size = 'md' as 'sm' | 'md' | 'lg' }) {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-6 h-6 border-2',
    lg: 'w-8 h-8 border-3',
  };

  return (
    <div className="flex items-center justify-center py-8">
      <div className={`${sizeClasses[size]} border-blue-500 border-t-transparent rounded-full animate-spin`} />
    </div>
  );
}
